import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './split-button.module.css';
import { Icon } from 'assets/icons';

/**
 * A split button: a main action that fires on click, plus a caret that opens a
 * dropdown of the remaining actions.
 *
 * actions: grouped action arrays (ContextMenu format). The first visible action
 * of the first non-empty group becomes the main action; the rest go in the menu.
 */
export const SplitButton = ({
  actions = [],
  callback,
  disabled = false,
  type = 'primary',
  separator_before_last = false,
  hide = false
}) => {
  const [show_menu, setShowMenu] = useState(false);
  const wrapper_ref = useRef(null);

  // Flatten the grouped actions, dropping hidden ones, preserving order. Also
  // accepts a flat (ungrouped) list, since some pages configure actions that way.
  const visible_actions = (actions || [])
    .reduce((flat, group) => flat.concat(group), [])
    .filter((action) => action && !action.hidden);

  // Close the dropdown when clicking outside the split button.
  useEffect(() => {
    if (!show_menu) return;
    const handleOutsideClick = (event) => {
      if (wrapper_ref.current && !wrapper_ref.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [show_menu]);

  if (!visible_actions.length) return null;

  // Used to collapse the button entirely (e.g. bulk actions with no selection).
  if (hide) return null;

  const [main_action, ...rest] = visible_actions;

  const runAction = (action) => {
    if (action.action) action.action();
    else callback(action.value);
  };

  const handleMainClick = () => {
    runAction(main_action);
  };

  const handleMenuAction = (action) => {
    setShowMenu(false);
    runAction(action);
  };

  return (
    <div
      className={cx(styles.splitButton, styles[type], disabled && styles.disabled)}
      ref={wrapper_ref}
    >
      <button
        type="button"
        className={styles.mainAction}
        onClick={handleMainClick}
        disabled={disabled}
      >
        {main_action.label}
      </button>
      {rest.length > 0 && (
        <>
          <button
            type="button"
            className={styles.caret}
            aria-label="More actions"
            aria-expanded={show_menu}
            onClick={() => setShowMenu((open) => !open)}
            disabled={disabled}
          >
            <Icon name="expand_down" width={18} height={18} />
          </button>
          {show_menu && (
            <div className={styles.menu} role="menu">
              {rest.map((action, index) => (
                <React.Fragment key={action.value || index}>
                  {separator_before_last && index === rest.length - 1 && rest.length > 1 && (
                    <div className={styles.separator} />
                  )}
                  <div
                    role="menuitem"
                    className={cx(styles.menuItem, styles[action.color])}
                    onClick={() => handleMenuAction(action)}
                  >
                    {action.label}
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SplitButton;
